/**
 * @file http请求控制
 */
import { Toast } from 'vant'
import { getServerHost } from '@/utils/host'
import { jsontoString, stringToJson } from '@/utils/tool'
import {
  encryptionParameters,
  getPostSignKey,
  getSignKey,
  getParams,
} from './httpSign'
import {
  getMiToken,
  getLoginTime,
  delCookie,
  toplupeToken,
  loginTimeKey,
} from '@/utils/login'
import { region } from '@/utils/utils'
import { platformType, customMadeQuery } from '@/utils/platform'
import { globalVariable } from '@/utils/exportVariable'
import {
  apiRequestBeforeTrack,
  headersLanguageHack,
  apmTrackServerError,
  errorTrack,
  apiRequestTimeTrack,
  fixSource,
  getClientVersion,
} from './request_helper'
import { apiCache, API_CACHE_TYPE } from '@/utils/apiCache'
import cloneDeep from 'lodash/cloneDeep'
import { apiInit } from '@/api/init'
import JSONBigInt from 'json-bigint'

let axios = null
let instance = null
let bigJSONInstance = null

const apiRequestMaps = new Map()
const apiRequestConfigMaps = new Map()
const toastMaps = []

// 接口路径前缀，需与后端路由一致（后端未改则为 kachibuy，改完后端后可改为 toplupe）
const API_PATH = '/api/kachibuy'
export function appendApiUrl(path) {
  return API_PATH + path
}

const JSONBig = JSONBigInt({
  // 将大数转换为字符串（推荐）
  storeAsString: true,
  // 或者使用 BigInt（需要现代浏览器支持）
  // useNativeBigInt: true
})

// 自定义处理报错的接口列表、不适用toast
const CUSTOM_ERROR_REQUEST = [
  '/api/verify/code/send',
  '/api/verify/code/bindOrUpdate',
  appendApiUrl('/activity/save/coupon/after/pay'),
]
export function setAxios(axiosInstance, i18n) {
  axios = axiosInstance

  // 1. 备份全局原始的 transformResponse（可选，如需保留全局配置后续恢复）
  const originalGlobalTransformResponse = [
    ...(axiosInstance.defaults.transformResponse || []),
  ]

  axiosInstance.defaults.transformResponse = []

  bigJSONInstance = axiosInstance.create({
    baseURL: getServerHost(),
    timeout: process.env.VUE_APP_ENV === 'prod' ? 20000 : 60000,
    transformResponse: [
      function (data) {
        if (typeof data === 'string' && data) {
          try {
            return JSONBig.parse(data)
          } catch (e) {
            return data
          }
        }
        return data
      },
      ...originalGlobalTransformResponse,
    ],
  })
  configInstance(bigJSONInstance)

  instance = axios.create({
    baseURL: getServerHost(),
    timeout: process.env.VUE_APP_ENV === 'prod' ? 20000 : 60000,
    transformResponse: originalGlobalTransformResponse,
  })
  configInstance(instance)

  function configInstance(instance_) {
    instance_.defaults.headers.post['Content-Type'] =
      'application/x-www-form-urlencoded'

    const serverErrorTextMaps = {
      [appendApiUrl('/order/create/order')]: i18n.t('common.serverErrorMsg'),
    }

    const midasbuyUrls = ['/api/midasbuy/account', '/api/midasbuy/exchang']

    const defaultErrorText = i18n.t('common.noNetwork')
    let initAPICallStatus = false

    // 添加请求拦截器
    instance_.interceptors.request.use(
      async (config) => {
        const { url, headers } = config
        if (process.client) {
          // 获取缓存数据
          const cacheDatas = apiCache.get(config)
          // 如果有缓存数据，则取消当前的请求，将缓存数据传到cancel方法中
          if (cacheDatas) {
            const source = axios.CancelToken.source()
            config.cancelToken = source.token
            try {
              source.cancel(
                JSON.stringify({
                  type: API_CACHE_TYPE,
                  data: cacheDatas,
                })
              )
            } catch (e) {
              //eslint-disable-next-line
              console.log(e)
            }
            return config
          } else {
            try {
              apiCache.init(cloneDeep(config))
              delete config.headers.apiCache
            } catch (e) {
              //eslint-disable-next-line
              console.log(e)
            }
          }
        }

        const loginTime = getLoginTime()
        // 根据之前接口约定，pwa端需要传'H5'字符串
        let kachiEnd = platformType()

        if (kachiEnd === 'PWA') {
          kachiEnd = 'H5'
        }

        // 对SKU接口做特殊处理、定制显示@胡冬
        let setSkuHeader = hackRequestSkuHeaderConfig(url)
        if (setSkuHeader) {
          kachiEnd = 'PrdTest6'
        }

        const customHeaders = headers.signatureData
          ? encryptionParameters({
              url,
              data: headers.signatureData,
            })
          : encryptionParameters(config)
        delete config.headers.signatureData
        const headerParams = {
          'Kicoin-Token': getMiToken(),
          country: region(),
          language: headersLanguageHack(),
          'kachi-end': kachiEnd, // 根据客户端类型获取不同响应数据
          'Login-Time':
            loginTime && loginTime !== 'undefined' && loginTime !== ''
              ? loginTime
              : '',
          ...customHeaders,
          'X-Version': getClientVersion(),
        }
        for (let key in headerParams) {
          if (headerParams[key]) {
            config.headers[key] = headerParams[key]
          }
        }
        apiRequestMaps.set(url, +new Date())
        showLoading(url)
        // api请求打点
        apiRequestBeforeTrack(config)
        return config
      },
      (error) => {
        closeLoading()
        showErrorToast(error.message)
        return Promise.reject(error)
      }
    )

    // 添加响应拦截器
    instance_.interceptors.response.use(
      (response) => {
        const {
          data: { status, code },
          status: statusCode,
          config: { url },
        } = response

        const startTs = apiRequestMaps.get(url) || 0
        const duration = +new Date() - startTs
        apiRequestTimeTrack(url, duration)
        if (statusCode == 200) {
          closeLoading(url)

          if (
            (status != undefined && status != 200) ||
            (code != undefined && code != 200)
          ) {
            closeLoading(url)
            // 1006:时间戳不对
            // 1007:签名过期，需要重新调用init方法获取签名auth
            if (
              [1006, 1007].includes(status * 1) &&
              !initAPICallStatus &&
              midasbuyUrls.indexOf(url) === -1
            ) {
              initAPICallStatus = true
              apiInit(region())
                .then((apiSign) => {
                  if (apiSign && Object.keys(apiSign).length) {
                    const store = globalVariable?.store || {}
                    store.commit('common/setApiSign', apiSign)
                  }
                })
                .finally(() => {
                  initAPICallStatus = false
                })
            }
            if (status === 1208) {
              delCookie(toplupeToken)
              delCookie(loginTimeKey)
              const store = globalVariable?.store || {}
              store.commit('setUserInfo', null)
            }
            if (
              url === appendApiUrl('/order/create/order') ||
              url === appendApiUrl('/order/create/one-yuan/order')
            ) {
              // 针对下单接口
              const code = code || status
              const errText = i18n.te(`apiErrorTips.${code}`)
                ? i18n.t(`apiErrorTips.${code}`)
                : i18n.t(`apiErrorTips.default`)
              showErrorToast(errText, url, code)
            } else {
              // 520 为风控的status，此种状态不需要弹错误toast
              // 账号风控：1207 —— 临时封禁，禁止下单 1208: 永久封禁账号，禁止登录 & 禁止下单
              const code = code || status
              if (status !== 520 && ![1207, 1208].includes(code)) {
                let errText = ''

                // todo 新增的code值、跟服务器约定直接弹出msg即可@志宝
                if (status === -99999999) {
                  errText = response.data.msg
                } else {
                  errText = i18n.te(`apiErrorTips.${code}`)
                    ? i18n.t(`apiErrorTips.${code}`)
                    : i18n.t(`apiErrorTips.default`)
                }

                showErrorToast(
                  errText ||
                    serverErrorTextMaps[response.config.url] ||
                    defaultErrorText,
                  url
                )
              }
            }
            // return Promise.reject({code: status || code, msg: msg || info})
            if (midasbuyUrls.indexOf(url) > -1) {
              const midasbuyCode = code || status
              // 米大师的接口如果返回非以下状态需要上报，否则不上报APM
              const notNeedToApmCodes = [200, 1006, 1007]
              if (notNeedToApmCodes.indexOf(midasbuyCode * 1) === -1) {
                apmTrackServerError({
                  message: response.data?.message,
                  code: midasbuyCode,
                  extraInfos: response.config,
                })
              }
            } else {
              apmTrackServerError({
                message: response.data.msg,
                code: code || status,
                extraInfos: response.config,
              })
            }
          }
        }
        if (process.client) {
          // 缓存数据
          apiCache.set(cloneDeep(response))
        }
        return response
      },
      (error) => {
        if (error && error.response) {
          apmTrackServerError({
            message: error.message,
            code: error.response.status,
            extraInfos: error.response.config,
          })
        }
        closeLoading()
        showErrorToast(error && error.message)
        return Promise.reject(error)
      }
    )
  }

  function showLoading(url) {
    const { showReqLoading = true } = globalVariable.store.state
    if (url && apiRequestConfigMaps.get(url).showLoading && showReqLoading) {
      const toast = Toast.loading({
        duration: 0,
        message: 'Loading...',
        forbidClick: true,
        className: 'api-loading',
        getContainer: 'body',
        iconPrefix: 'loading-img',
        icon: require('@/assets/images/common/api-loading.webp'),
      })
      toastMaps.push(toast)
    }
  }

  function closeLoading(url) {
    if (url && apiRequestConfigMaps.get(url).closeLoading) {
      toastMaps.forEach((toast) => {
        toast.clear(toast)
      })
    }
  }

  function showErrorToast(msg, url, code) {
    if (url && apiRequestConfigMaps.get(url).showErrorToast) {
      if (
        url === appendApiUrl('/order/create/order') ||
        url === appendApiUrl('/order/create/one-yuan/order')
      ) {
        const errCode_noNeedToToast = [
          '21001', // 支付密码校验失败
          '21004', // 密码验证错误次数达到上限
          '20002', // 账户已冻结
          '20009', // 余额不足
          '20013', //24小时内消费频次过高
          '20014', //24小时内消费金额过高
          '1207', // 账户临时封禁，短时间消费过多
          '1208', // 账户永久封禁
        ]
        if (errCode_noNeedToToast.indexOf(String(code)) < 0) {
          const toast = Toast.fail(msg)
          toastMaps.push(toast)
        }
      } else if (CUSTOM_ERROR_REQUEST.includes(url)) {
        // 单独处理
        return
      } else {
        const toast = Toast.fail(msg || defaultErrorText)
        toastMaps.push(toast)
      }
    }
  }
}

function setConfig(configOptions = {}, options = {}) {
  const defaultConfigs = {
    showLoading: true,
    showErrorToast: true,
    closeLoading: true,
  }
  apiRequestConfigMaps.set(options.url, { ...defaultConfigs, ...configOptions })
}

function hackRequestSkuHeaderConfig(url) {
  const addrs = appendApiUrl('/config/query/sku')
  const _plt = platformType()

  // 限制只能GT\H5\PC三端、其他逻辑不变
  if (!['GT', 'H5', 'PC'].includes(_plt)) return false
  return url === addrs && customMadeQuery()
}

function axiosInstance(configOptions = {}, url = '') {
  const { useBigJSON } = configOptions
  // 目前发现是orderNo是大数字，默认解析出来有问题，这里针对order相关接口处理精度问题
  const containsOrder = url.indexOf('/order') !== -1
  return useBigJSON || containsOrder ? bigJSONInstance : instance
}

const request = {
  get(options = {}, configOptions = {}, headersConfig = {}) {
    setConfig(configOptions, options)
    const { url } = options
    return new Promise((resolve, reject) => {
      instance({
        method: 'get',
        url,
        headers: {
          'Kicoin-Sign': getSignKey(getParams(url)),
          'Cache-Control': 'no-cache',
          ...headersConfig,
        },
        params: {
          sign: getSignKey(getParams(url)),
        },
      })
        .then((response) => {
          requestResolveCallback(response, resolve, reject)
        })
        .catch((err) => {
          requestRejectCallback(err, options, url, resolve, reject)
        })
    })
  },
  post(options = {}, configOptions = {}, headersConfig = {}) {
    setConfig(configOptions, options)
    let { baseURL, url, data = {} } = options
    data = fixSource(data)
    data.sign = getSignKey(data)
    return new Promise((resolve, reject) => {
      instance({
        method: 'post',
        baseURL,
        url,
        data: jsontoString(data),
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          ...headersConfig,
        },
      })
        .then((response) => {
          requestResolveCallback(response, resolve, reject)
        })
        .catch((err) => {
          requestRejectCallback(err, options, url, resolve, reject)
        })
    })
  },
  postJson(options = {}, configOptions = {}, headersConfig = {}) {
    setConfig(configOptions, options)
    let { baseURL, url, data = {} } = options
    data = fixSource(data)
    data = fixSource(data, 'activitySource')
    return new Promise((resolve, reject) => {
      axiosInstance(
        configOptions,
        url
      )({
        method: 'post',
        baseURL,
        url,
        headers: {
          'content-type': 'application/json',
          'Kicoin-Sign': getPostSignKey(data),
          ...headersConfig,
        },
        timeout: headersConfig.timeout ? headersConfig.timeout : undefined,
        data: { ...data, sign: getSignKey(data) },
      })
        .then((response) => {
          requestResolveCallback(response, resolve, reject)
        })
        .catch((err) => {
          if (url.indexOf('custom-config') === -1) {
            //eslint-disable-next-line
            console.log('[custom-config]postJson: ===>>>', err)
          }
          requestRejectCallback(err, options, url, resolve, reject)
        })
    })
  },
  postMultipart(options = {}, configOptions = {}) {
    setConfig(configOptions, options)
    const { url, data = {}, signatureData } = options
    return new Promise((resolve, reject) => {
      instance({
        method: 'post',
        url,
        headers: {
          'content-type':
            'multipart/form-data; boundary=----WebKitFormBoundarykT0Mw72tnXPHYbVM',
          signatureData,
        },
        data,
      })
        .then((response) => {
          requestResolveCallback(response, resolve, reject)
        })
        .catch((err) => {
          requestRejectCallback(err, options, url, resolve, reject)
        })
    })
  },
}

export default request
// 成功后的处理方法
function requestResolveCallback(response, resolve, reject) {
  const {
    status,
    statusText,
    data: { data = [], status: code },
  } = response || {}
  if (status === 200 && code === 200) {
    resolve(data)
  } else {
    reject({
      status,
      statusText,
      code,
    })
  }
}
// 失败后的处理方法
function requestRejectCallback(err, options, url, resolve, reject) {
  const { code = 0, message = '' } = err || {}
  try {
    // 获取缓存数据
    if (axios.isCancel(err)) {
      const cancle = stringToJson(err.message)
      if (cancle.type === API_CACHE_TYPE) {
        return resolve(cancle.data)
      } else {
        errorTrack(options, 'fe', message, code, url)
        reject({
          code,
          message,
        })
      }
    } else {
      errorTrack(options, 'fe', message, code, url)
      reject({
        code,
        message,
      })
    }
  } catch (e) {
    //eslint-disable-next-line
    console.log(e)
  }
}
